import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { usePurchase } from "@/context/PurchaseContext";
import { API } from "@/lib/api";
import { MiniWaveform } from "@/components/MiniWaveform";
import { IncludedFormats } from "@/components/IncludedFormats";
import { Play, Pause, ShoppingBag, Check, Loader2, Headphones } from "lucide-react";

const PseudoWave = ({ active }) => (
  <div className="hidden lg:flex items-end gap-[2px] h-8 w-32 opacity-60">
    {Array.from({ length: 32 }).map((_, i) => (
      <div key={i} className="flex-1 rounded-sm"
        style={{ height: `${20 + Math.abs(Math.sin(i * 0.7)) * 80}%`, background: active ? "#e2ff31" : "#3a3a3a" }} />
    ))}
  </div>
);

export const TrackRow = ({ track, list, owned }) => {
  const { current, isPlaying, playTrack } = usePlayer();
  const { openBuy, loadingId } = usePurchase();
  const isCurrent = current?.id === track.id;
  const loading = loadingId === track.id;

  return (
    <div data-testid={`track-row-${track.id}`}
      className="group grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1.5fr_1fr_auto_auto] items-center gap-4 px-3 md:px-4 py-3 rounded-xl hover:bg-[#121212] transition-colors border border-transparent hover:border-[#2a2a2a]">
      <div className="relative w-12 h-12 md:w-14 md:h-14 shrink-0">
        <img src={`${API}${track.cover_url}`} alt={track.title}
          className={`w-full h-full rounded-lg object-cover border border-[#2a2a2a] ${track.sold ? "opacity-50" : ""}`} />
        <button data-testid={`play-track-${track.id}`} onClick={() => playTrack(track, list)}
          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ opacity: isCurrent ? 1 : undefined }}>
          {isCurrent && isPlaying ? <Pause className="w-5 h-5 text-[#e2ff31]" fill="currentColor" />
            : <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />}
        </button>
      </div>

      <div className="min-w-0">
        <p className={`font-medium truncate ${isCurrent ? "text-[#e2ff31]" : "text-[#f3f4f6]"} ${track.sold ? "line-through opacity-60" : ""}`}>{track.title}</p>
        <div className="flex items-center gap-2 text-sm text-[#9ca3af]">
          <span className="truncate">{track.producer}</span>
          <span className="flex items-center gap-1 text-xs font-mono text-[#6b7280] shrink-0" data-testid={`play-count-${track.id}`}>
            <Headphones className="w-3 h-3" /> {(track.play_count || 0).toLocaleString()}
          </span>
        </div>
      </div>

      <div className="hidden md:flex items-center gap-4 text-xs font-mono text-[#6b7280]">
        {track.genre && <span className="px-2 py-1 rounded-full bg-[#1e1e1e] text-[#9ca3af]">{track.genre}</span>}
        {track.bpm ? <span>{track.bpm} BPM</span> : null}
        {track.music_key ? <span>{track.music_key}</span> : null}
        {!track.sold && <IncludedFormats track={track} />}
      </div>

      <div className="hidden md:block w-32">
        {track.waveform && track.waveform.length
          ? <MiniWaveform peaks={track.waveform} bars={32} active={isCurrent} className="h-8" />
          : <PseudoWave active={isCurrent} />}
      </div>

      <div className="flex items-center justify-end">
        {owned ? (
          <span className="flex items-center gap-1 text-sm text-[#22c55e] font-medium px-3"><Check className="w-4 h-4" /> Owned</span>
        ) : track.sold ? (
          <span data-testid={`sold-badge-${track.id}`} className="text-sm font-bold text-[#ef4444] border border-[#ef4444]/40 rounded-full px-4 py-2 uppercase tracking-wide">Sold</span>
        ) : (
          <button data-testid={`buy-track-${track.id}`} onClick={() => openBuy(track)} disabled={loading}
            className="flex items-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] active:scale-95 transition-all font-semibold text-sm px-4 py-2 shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
            ${track.price?.toFixed(2)}
          </button>
        )}
      </div>
    </div>
  );
};
